import { useEffect, useRef, memo, useState } from "react";

function TradingViewWidget({ symbol = "FX:EURUSD", height = 460, mini = false }) {
  const containerRef = useRef(null);
  // Effective rendered height (mobile squaring).
  const [effectiveHeight, setEffectiveHeight] = useState(height);
  // Lazy-mount: only inject the TradingView script when the widget is
  // close to or inside the viewport. Massive perf win when the widget
  // lives below the fold (LiveMarketsPanel sits ~7 sections deep).
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const compute = () => {
      const w = containerRef.current?.clientWidth || 0;
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const sq = Math.min(Math.max(w, 280), height);
        setEffectiveHeight(sq);
      } else {
        setEffectiveHeight(height);
      }
    };
    compute();
    window.addEventListener("resize", compute, { passive: true });
    return () => window.removeEventListener("resize", compute);
  }, [height]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "400px 0px" } // start loading 400px before entering viewport
    );
    io.observe(node);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !inView) return;
    // Safe DOM clear — no innerHTML assignment (XSS-hardening).
    while (container.firstChild) container.removeChild(container.firstChild);

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled || !containerRef.current) return;

      // TradingView's free embed always renders a 32px branding/copyright bar at the
      // bottom of the iframe. Strategy: render iframe 32px TALLER than the visible
      // container, then position it absolutely so the branding sits BELOW the visible
      // area and is clipped by `overflow:hidden` on the parent — zero gap, no cover.
      const innerH = effectiveHeight + 32;
      const inner = document.createElement("div");
      inner.className = "tradingview-widget-container__widget";
      inner.style.position = "absolute";
      inner.style.top = "0";
      inner.style.left = "0";
      inner.style.right = "0";
      inner.style.height = `${innerH}px`;
      inner.style.width = "100%";
      inner.style.lineHeight = "0";
      inner.style.fontSize = "0";
      containerRef.current.appendChild(inner);

      const script = document.createElement("script");
      script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
      script.type = "text/javascript";
      script.async = true;
      // Use textContent (not innerHTML) — safe for inline-script payloads, the
      // content is a JSON config object we fully control (no user input).
      script.textContent = JSON.stringify({
        autosize: false,
        width: "100%",
        height: innerH,
        symbol,
        interval: mini ? "30" : "60",
        timezone: "Etc/UTC",
        theme: "dark",
        style: mini ? "3" : "1",  // 3 = area chart for mini
        locale: "en",
        enable_publishing: false,
        allow_symbol_change: false,
        backgroundColor: "#050b0e",
        gridColor: "rgba(170,222,235,0.06)",
        hide_top_toolbar: mini,
        hide_legend: mini,
        hide_side_toolbar: mini,
        save_image: false,
        calendar: false,
        withdateranges: !mini,
        toolbar_bg: "#050b0e",
        support_host: "https://www.tradingview.com",
      });
      containerRef.current.appendChild(script);
    }, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (containerRef.current) {
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
      }
    };
  }, [symbol, mini, effectiveHeight, inView]);

  return (
    <div
      data-testid={mini ? "markets-chart-mini" : "markets-chart-main"}
      ref={containerRef}
      className="tradingview-widget-container w-full"
      style={{ height: effectiveHeight, minHeight: effectiveHeight, maxHeight: effectiveHeight, position: "relative", overflow: "hidden", lineHeight: 0, fontSize: 0, background: "#050b0e" }}
    />
  );
}

export default memo(TradingViewWidget);
